import { AfterViewInit, Component, ElementRef, ErrorHandler, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { SituacaoParcelaFeriasEnum } from 'src/app/components/shared/enums/situacao-parcela-ferias.enum';
import { OPCOES_PERIODO_AQUISITIVO, PeriodoAquisitivo } from 'src/app/components/shared/models/periodo-aquisitivo';
import { formatDDMMYYYY, getDateFromYYYYMMDD, getValidDateDDMMYYYY } from 'src/app/components/shared/date-utils';
import { ExercicioSelecionado, ExerciciosService, FeriasEspeciaisJudiciaisComParcelas, ParcelaFerias, ParcelaFeriasEspecialJudicialComParcelas } from 'src/app/services/exercicios.service';
import { Message } from 'src/app/components/shared/message';
import { formatarMensagem, generateUID } from 'src/app/components/shared/string-utils';
import { differenceInDays } from 'date-fns';
import { Subscription } from 'rxjs';

interface DadosExercicioJudicial {
    numeroProcesso: string;
    sentencaJudicial: string;
    quantidadeDias: number | null;
    quantidadeMaximaParcelas: number | null;
    periodoAquisitivo: PeriodoAquisitivo | null;
    extraordinario: boolean;
    dataFimPeriodoProgramacao: string;
    declaracaoAcaoJudicial: boolean;
    declaracao: boolean;
}

interface ParcelaJudicialComParcelas {
    id: number | null;
    idParcelaPai: number | null;
    idTemporario: string | null;
    idTemporarioParcelaPai: string | null;
    ordem: number;
    geracao: number;
    dataInicio: string;
    dataInicioCalendarioControl: FormControl<Date | null>;
    quantidadeDias: number | null;
    adiantamentoSalarial: boolean;
    abonoPecuniario: boolean;
    adiantamentoDecimoTerceiro: boolean;
    parcelasDesconto: number | null;
    situacao: string;
    dataInicioDesabilitada: boolean;
    quantidadeDiasDesabilitada: boolean;
    quantidadeDiasAutomatica: boolean;
    selecionadaParaInterrupcao: boolean;
    dataInterrupcao: Date | undefined;
    motivoLegal: string;
    justificativaInterrupcao: string;
    documentoLegal: string;
    dataPublicacaoDocumentoLegal: Date | undefined;
    dataMinimaInterrupcao: Date | null;
    dataMinimaReprogramacao: Date | null;
    validacao: {
        dataInterrupcaoInvalida: boolean;
        dataInterrupcaoForaPeriodoFerias: boolean;
        dataPublicacaoInvalida: boolean;
        justificativaInterrupcaoInvalida: boolean;
    };
    tocado: {
        dataInicio: boolean;
        quantidadeDias: boolean;
    };
}

type CampoExercicio = keyof DadosExercicioJudicial;
type CampoParcela = 'dataInicio' | 'quantidadeDias';

declare const core: any;

interface InstanciaDsgov {
    destroy?: () => void;
}

const REPETICAO_EXTENSA_PROCESSO = /(\d)\1{5,}/;
const QUANTIDADE_MAXIMA_DIAS_JUDICIAIS = 90;
const QUANTIDADE_MAXIMA_PARCELAS_CLT = 3;
const QUANTIDADE_MAXIMA_PARCELAS_DEMAIS_REGIMES = 6;
const QUANTIDADE_PARCELAS_RAIO_X = 2;
const SITUACAO_FUNCIONAL_EXCEPCIONAL = 7;
const ROTA_CONSULTAR_EXERCICIOS = '/secure/gerenciar-ferias/consultar-exercicios';

function dataIsoValida(valor: string): boolean {
    const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
    if (!partes) {
        return false;
    }

    const ano = Number(partes[1]);
    const mes = Number(partes[2]);
    const dia = Number(partes[3]);
    const data = new Date(ano, mes - 1, dia);

    return data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;
}

@Component({
    selector: 'app-ferias-especiais-judiciais-com-parcelas',
    templateUrl: './ferias-especiais-judiciais-com-parcelas.component.html',
    styleUrls: ['./ferias-especiais-judiciais-com-parcelas.component.scss']
})
export class FeriasEspeciaisJudiciaisComParcelasComponent extends FeriasBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('dsgovInterrupcaoField', { read: ElementRef })
    private readonly dsgovInterrupcaoFields!: QueryList<ElementRef<HTMLElement>>;

    readonly dadosExercicio: DadosExercicioJudicial = {
        numeroProcesso: '',
        sentencaJudicial: '',
        quantidadeDias: null,
        quantidadeMaximaParcelas: null,
        periodoAquisitivo: null,
        extraordinario: false,
        dataFimPeriodoProgramacao: '',
        declaracaoAcaoJudicial: false,
        declaracao: false
    };
    readonly parcelas: ParcelaJudicialComParcelas[] = [];
    readonly opcoesPeriodoAquisitivo = OPCOES_PERIODO_AQUISITIVO;
    readonly itensWizard: MenuItem[] = [{ label: 'Cadastro Processo' }, { label: 'Alterar Parcelas e Confirmar' }];
    opcoesQuantidadeMaximaParcelas: number[] = [];

    exercicioSelecionado?: ExercicioSelecionado;
    wizardActiveIndex = 0;
    submetidoExercicio = false;
    submetidoParcelas = false;
    houvePreenchimento = false;
    declaracaoTocada = false;
    dataFimPeriodoProgramacaoCalendario: Date | null = null;
    dataInicioPeriodoProgramacaoMinimaCalendario: Date | null = null;
    dataFimPeriodoProgramacaoMaximaCalendario: Date | null = null;
    calculandoPeriodoProgramacao = false;
    erroCalculoPeriodoProgramacao = false;

    private dataFimPeriodoProgramacaoCalculado = '';
    private periodoProgramacaoSubscription?: Subscription;

    private readonly camposExercicioTocados = new Set<CampoExercicio>();
    private readonly dadosOriginaisInterrupcao = new WeakMap<
        ParcelaJudicialComParcelas,
        {
            quantidadeDias: number;
            dataFim: Date | null;
            situacao: string;
            dataInterrupcaoAplicada: string | null;
        }
    >();
    private dsgovInterrupcaoFieldsSubscription?: Subscription;
    private instanciasDsgov: InstanciaDsgov[] = [];

    constructor(
        private readonly exerciciosService: ExerciciosService,
        private readonly errorHandler: ErrorHandler,
        alertaMsgService: AlertMessageService,
        private readonly confirmationService: ConfirmationService,
        private readonly router: Router
    ) {
        super(alertaMsgService);
    }

    ngOnInit(): void {
        const exercicioSelecionado = this.router.getCurrentNavigation()?.extras?.state?.['data'] ?? history.state?.data ?? JSON.parse(sessionStorage.getItem('consultaGerencialExercicioSelecionado') ?? 'null');
        this.exercicioSelecionado = exercicioSelecionado ? { ...exercicioSelecionado } : undefined;

        const periodoAquisitivo = this.exercicioSelecionado?.periodoAquisitivo;
        if (periodoAquisitivo === 'ANC' || periodoAquisitivo === 'PAQ') {
            this.dadosExercicio.periodoAquisitivo = periodoAquisitivo;
        }

        this.dadosExercicio.extraordinario = this.exercicioSelecionado?.extraordinario === true;
        this.dadosExercicio.quantidadeDias = this.exercicioSelecionado?.saldoDias ?? null;
        this.dataFimPeriodoProgramacaoCalculado = this.exercicioSelecionado?.dataFimPeriodoProgramacao ?? '';
        this.dadosExercicio.dataFimPeriodoProgramacao = this.dataFimPeriodoProgramacaoCalculado;
        this.dataFimPeriodoProgramacaoCalendario = this.paraDataCalendario(this.dataFimPeriodoProgramacaoCalculado);
        this.dataInicioPeriodoProgramacaoMinimaCalendario = this.paraDataCalendario(this.dataInicioPeriodoProgramacaoMinima);
        this.sincronizarPeriodoProgramacao();

        const quantidadeMaximaParcelas = this.exercicioSelecionado?.quantidadeMaximaParcelas;
        if (quantidadeMaximaParcelas !== undefined) {
            this.dadosExercicio.quantidadeMaximaParcelas = quantidadeMaximaParcelas;
        }

        if (this.permiteSomenteParcelasRaioX) {
            this.dadosExercicio.quantidadeMaximaParcelas = QUANTIDADE_PARCELAS_RAIO_X;
        }

        this.atualizarOpcoesQuantidadeMaximaParcelas();
        this.carregarParcelasDoExercicio();
        this.calcularPeriodoProgramacao(this.dadosExercicio.periodoAquisitivo);
    }

    ngAfterViewInit(): void {
        this.dsgovInterrupcaoFieldsSubscription = this.dsgovInterrupcaoFields.changes.subscribe(() => this.atualizarInstanciasDsgov());
        this.atualizarInstanciasDsgov();
    }

    ngOnDestroy(): void {
        this.periodoProgramacaoSubscription?.unsubscribe();
        this.dsgovInterrupcaoFieldsSubscription?.unsubscribe();
        this.destruirInstanciasDsgov();
    }

    private atualizarInstanciasDsgov(): void {
        this.destruirInstanciasDsgov();
        this.instanciasDsgov = this.dsgovInterrupcaoFields.map(elemento => {
            const container = elemento.nativeElement;
            return new core.BRInput('br-input', container);
        });
    }

    private destruirInstanciasDsgov(): void {
        this.instanciasDsgov.forEach(instancia => instancia.destroy?.());
        this.instanciasDsgov = [];
    }

    trackByParcela(_indice: number, parcela: ParcelaJudicialComParcelas): string {
        return parcela.id !== null ? `id-${parcela.id}` : `temporaria-${parcela.idTemporario}`;
    }

    parcelaPodeSerInterrompida(parcela: ParcelaJudicialComParcelas): boolean {
        const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcela.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcela.situacao);
        const parcelaPersistidaElegivel = parcela.id !== null && (situacao === SituacaoParcelaFeriasEnum.USUFRUIDA || situacao === SituacaoParcelaFeriasEnum.EM_GOZO);
        const parcelaNovaElegivel = parcela.id === null && parcela.geracao > 0 && parcela.idTemporario !== null;

        return parcela.quantidadeDias !== null && parcela.quantidadeDias > 1 && dataIsoValida(parcela.dataInicio) && !this.possuiParcelaFilha(parcela) && (parcelaPersistidaElegivel || parcelaNovaElegivel);
    }

    selecionarParcelaParaInterromper(parcela: ParcelaJudicialComParcelas): void {
        if (!this.parcelaPodeSerInterrompida(parcela)) {
            return;
        }

        const indiceParcela = this.parcelas.indexOf(parcela);
        const dataFimIso = this.dataFimParcela(indiceParcela);
        const dataFim = this.paraDataCalendario(dataFimIso);
        if (indiceParcela === -1 || parcela.quantidadeDias === null || !dataFim) {
            return;
        }

        this.houvePreenchimento = true;
        parcela.selecionadaParaInterrupcao = true;
        this.dadosOriginaisInterrupcao.set(parcela, {
            quantidadeDias: parcela.quantidadeDias,
            dataFim: new Date(dataFim),
            situacao: parcela.situacao,
            dataInterrupcaoAplicada: null
        });
        this.criarParcelaFilha(parcela);
    }

    private possuiParcelaFilha(parcela: ParcelaJudicialComParcelas): boolean {
        return this.parcelas.some(filha => this.ehParcelaFilha(parcela, filha));
    }

    private ehParcelaFilha(parcela: ParcelaJudicialComParcelas, filha: ParcelaJudicialComParcelas): boolean {
        if (filha.geracao !== parcela.geracao + 1) {
            return false;
        }

        return parcela.id !== null ? filha.idParcelaPai === parcela.id : parcela.idTemporario !== null && filha.idTemporarioParcelaPai === parcela.idTemporario;
    }

    private parcelaFilhaPendente(parcela: ParcelaJudicialComParcelas): ParcelaJudicialComParcelas | undefined {
        return this.parcelas.find(filha => filha.id === null && this.ehParcelaFilha(parcela, filha));
    }

    private parcelaPaiInterrompida(parcela: ParcelaJudicialComParcelas): ParcelaJudicialComParcelas | undefined {
        return this.parcelas.find(parcelaPai => {
            if (!this.ehParcelaFilha(parcelaPai, parcela)) {
                return false;
            }

            const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcelaPai.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcelaPai.situacao);
            return situacao === SituacaoParcelaFeriasEnum.INTERROMPIDA;
        });
    }

    private permiteExcluirParcelaFilhaInterrompida(parcela: ParcelaJudicialComParcelas): boolean {
        if (parcela.geracao === 0 || parcela.id === null || parcela.selecionadaParaInterrupcao || this.ehParcelaCancelada(parcela) || this.possuiParcelaFilha(parcela)) {
            return false;
        }

        const parcelaPai = this.parcelaPaiInterrompida(parcela);
        return parcelaPai !== undefined && !parcelaPai.selecionadaParaInterrupcao && !this.parcelas.some(outraParcela => outraParcela !== parcela && this.ehParcelaFilha(parcelaPai, outraParcela));
    }

    private criarParcelaFilha(parcela: ParcelaJudicialComParcelas): void {
        const indicePai = this.parcelas.indexOf(parcela);
        if ((parcela.id === null && parcela.idTemporario === null) || indicePai === -1 || this.parcelaFilhaPendente(parcela)) {
            return;
        }

        const parcelaFilha = this.criarParcela({
            idParcelaPai: parcela.id,
            idTemporarioParcelaPai: parcela.id === null ? parcela.idTemporario : null,
            ordem: parcela.ordem,
            geracao: parcela.geracao + 1,
            dataInicio: '',
            quantidadeDias: null,
            quantidadeDiasAutomatica: true,
            situacao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao
        });
        this.parcelas.splice(indicePai + 1, 0, parcelaFilha);
    }

    private excluirParcelaFilha(parcela: ParcelaJudicialComParcelas): void {
        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        if (!parcelaFilha) {
            return;
        }

        this.excluirParcelaFilha(parcelaFilha);
        this.dadosOriginaisInterrupcao.delete(parcelaFilha);
        const indiceParcelaFilha = this.parcelas.indexOf(parcelaFilha);
        if (indiceParcelaFilha !== -1) {
            this.parcelas.splice(indiceParcelaFilha, 1);
        }
    }

    private restaurarDadosOriginaisInterrupcao(parcela: ParcelaJudicialComParcelas): void {
        const dadosOriginais = this.dadosOriginaisInterrupcao.get(parcela);
        if (!dadosOriginais) {
            return;
        }

        parcela.quantidadeDias = dadosOriginais.quantidadeDias;
        parcela.situacao = dadosOriginais.situacao;

        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        if (parcelaFilha) {
            parcelaFilha.quantidadeDias = null;
            parcelaFilha.dataMinimaReprogramacao = this.dataInicioPeriodoProgramacaoMinimaCalendario;
        }
    }

    desistirSelecaoParcelaParaInterromper(parcela: ParcelaJudicialComParcelas): void {
        this.restaurarDadosOriginaisInterrupcao(parcela);
        this.excluirParcelaFilha(parcela);
        this.dadosOriginaisInterrupcao.delete(parcela);
        this.limparDadosInterrupcao(parcela);
    }

    private limparDadosInterrupcao(parcela: ParcelaJudicialComParcelas): void {
        parcela.motivoLegal = '';
        parcela.justificativaInterrupcao = '';
        parcela.dataInterrupcao = undefined;
        parcela.documentoLegal = '';
        parcela.dataPublicacaoDocumentoLegal = undefined;
        parcela.selecionadaParaInterrupcao = false;
        parcela.validacao.justificativaInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        parcela.validacao.dataPublicacaoInvalida = false;
    }

    dataPublicacaoChange(parcela: ParcelaJudicialComParcelas): void {
        parcela.validacao.dataPublicacaoInvalida = false;
        this.registrarAlteracao();
    }

    dataInterrupcaoChange(parcela: ParcelaJudicialComParcelas): void {
        parcela.validacao.dataInterrupcaoInvalida = false;
        parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    dataMaximaInterrupcao(parcela: ParcelaJudicialComParcelas): Date | null {
        return this.dadosOriginaisInterrupcao.get(parcela)?.dataFim ?? null;
    }

    blurDataPublicacaoChange(parcela: ParcelaJudicialComParcelas, event: Event): void {
        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataPublicacao = getValidDateDDMMYYYY(valorDigitado);

        parcela.validacao.dataPublicacaoInvalida = valorDigitado.length > 0 && !dataPublicacao;
        parcela.dataPublicacaoDocumentoLegal = dataPublicacao ?? undefined;
    }

    blurDataInterrupcaoChange(parcela: ParcelaJudicialComParcelas, event: Event): void {
        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataInterrupcao = getValidDateDDMMYYYY(valorDigitado);
        parcela.validacao.dataInterrupcaoInvalida = valorDigitado.length > 0 && !dataInterrupcao;

        parcela.dataInterrupcao = dataInterrupcao ?? undefined;

        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    inputDataPublicacaoChange(parcela: ParcelaJudicialComParcelas, event: Event): void {
        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataPublicacao = getValidDateDDMMYYYY(valorDigitado);
        parcela.dataPublicacaoDocumentoLegal = dataPublicacao ?? undefined;
        parcela.validacao.dataPublicacaoInvalida = !dataPublicacao && /^\d{2}\/\d{2}\/\d{4}$/.test(valorDigitado);
    }

    inputDataInterrupcaoChange(parcela: ParcelaJudicialComParcelas, event: Event): void {
        const valorDigitado = (event.target as HTMLInputElement).value;
        if (!valorDigitado) {
            parcela.dataInterrupcao = undefined;
            parcela.validacao.dataInterrupcaoInvalida = false;
            parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        } else {
            const dataInterrupcao = getValidDateDDMMYYYY(valorDigitado);
            parcela.dataInterrupcao = dataInterrupcao ?? undefined;
            parcela.validacao.dataInterrupcaoInvalida = !dataInterrupcao && /^\d{2}\/\d{2}\/\d{4}$/.test(valorDigitado);
            parcela.validacao.dataInterrupcaoForaPeriodoFerias = false;
        }

        this.atualizarParcelaFilhaPorInterrupcao(parcela);
    }

    getMsgDataInterrupcaoForaPeriodoFerias(parcela: ParcelaJudicialComParcelas): string {
        const dataFim = this.dataMaximaInterrupcao(parcela);
        return formatarMensagem(Message.ERRO_DATA_INTERRUPCAO_FORA_DO_INTERVALO, {
            dataMin: parcela.dataMinimaInterrupcao ? formatDDMMYYYY(parcela.dataMinimaInterrupcao) : '',
            dataFim: dataFim ? formatDDMMYYYY(dataFim) : ''
        });
    }

    onJustificativaChangeInterrupcao(event: Event, parcela: ParcelaJudicialComParcelas): void {
        const justificativa = (event.target as HTMLTextAreaElement).value;
        parcela.justificativaInterrupcao = justificativa ? justificativa.trim() : '';
        this.registrarAlteracao();
    }

    validarJustificativaInterrupcao(parcela: ParcelaJudicialComParcelas): void {
        const tamanho = parcela.justificativaInterrupcao.trim().length;
        parcela.validacao.justificativaInterrupcaoInvalida = tamanho > 0 && (tamanho < 10 || tamanho > 300);
    }

    private atualizarParcelaFilhaPorInterrupcao(parcela: ParcelaJudicialComParcelas): void {
        const parcelaFilha = this.parcelaFilhaPendente(parcela);
        const dadosOriginais = this.dadosOriginaisInterrupcao.get(parcela);
        if (!parcelaFilha || !dadosOriginais) {
            return;
        }

        const dataInicio = this.paraDataCalendario(parcela.dataInicio);
        const dataInterrupcao = parcela.dataInterrupcao;
        const dataMinima = parcela.dataMinimaInterrupcao;
        const dataMaxima = this.dataMaximaInterrupcao(parcela);
        const dataInterrupcaoIso = dataInterrupcao && !Number.isNaN(dataInterrupcao.getTime()) ? this.paraDataIso(dataInterrupcao) : null;
        const dataInvalida = !dataInicio || !dataInterrupcao || Number.isNaN(dataInterrupcao.getTime());
        const foraDoPeriodo = !!dataInterrupcao && ((dataMinima !== null && dataInterrupcao < dataMinima) || (dataMaxima !== null && dataInterrupcao > dataMaxima));
        const dataInterrupcaoAlterada = dataInterrupcaoIso !== dadosOriginais.dataInterrupcaoAplicada;

        parcela.validacao.dataInterrupcaoForaPeriodoFerias = foraDoPeriodo;
        if ((dataInterrupcaoAlterada || dataInvalida || foraDoPeriodo || parcela.validacao.dataInterrupcaoInvalida) && parcelaFilha.selecionadaParaInterrupcao) {
            this.desistirSelecaoParcelaParaInterromper(parcelaFilha);
        }

        if (dataInvalida || foraDoPeriodo || parcela.validacao.dataInterrupcaoInvalida || !dataInicio || !dataInterrupcao) {
            this.restaurarDadosOriginaisInterrupcao(parcela);
            dadosOriginais.dataInterrupcaoAplicada = null;
            return;
        }

        const diasAntesInterrupcao = differenceInDays(dataInterrupcao, dataInicio);
        const diasRestantes = dadosOriginais.quantidadeDias - diasAntesInterrupcao;
        if (diasAntesInterrupcao < 1 || diasRestantes < 1) {
            parcela.validacao.dataInterrupcaoForaPeriodoFerias = true;
            this.restaurarDadosOriginaisInterrupcao(parcela);
            dadosOriginais.dataInterrupcaoAplicada = null;
            return;
        }

        parcela.quantidadeDias = diasAntesInterrupcao;
        parcela.situacao = SituacaoParcelaFeriasEnum.INTERROMPIDA.descricao;
        parcelaFilha.dataMinimaReprogramacao = this.calcularDataMinimaReprogramacao(dataInterrupcao);
        parcelaFilha.quantidadeDias = diasRestantes;
        dadosOriginais.dataInterrupcaoAplicada = dataInterrupcaoIso;
        this.registrarAlteracao();
    }

    private calcularDataMinimaReprogramacao(dataInterrupcao?: Date): Date | null {
        if (!dataInterrupcao) {
            return this.dataInicioPeriodoProgramacaoMinimaCalendario;
        }

        const dataInterrupcaoMaisUm = new Date(dataInterrupcao.getFullYear(), dataInterrupcao.getMonth(), dataInterrupcao.getDate() + 1);
        const dataMinimaPeriodo = this.dataInicioPeriodoProgramacaoMinimaCalendario;

        return !dataMinimaPeriodo || dataInterrupcaoMaisUm > dataMinimaPeriodo ? dataInterrupcaoMaisUm : dataMinimaPeriodo;
    }

    private calcularDataMinimaInterrupcao(dataInicio: string): Date | null {
        const dataInicioParcela = this.paraDataCalendario(dataInicio);
        return dataInicioParcela ? new Date(dataInicioParcela.getFullYear(), dataInicioParcela.getMonth(), dataInicioParcela.getDate() + 1) : null;
    }

    get msgJustificativaObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_OBRIGATORIA;
    }

    get msgJustificativaInvalida(): string {
        return Message.ERRO_JUSTIFICATIVA_TAMANHO_INVALIDO;
    }

    private get existeInterrupcaoInvalida(): boolean {
        return this.parcelas.some(parcela => {
            if (!parcela.selecionadaParaInterrupcao) {
                return false;
            }

            const justificativa = parcela.justificativaInterrupcao.trim();
            const motivoLegal = parcela.motivoLegal.trim();
            const dataInterrupcao = parcela.dataInterrupcao;
            const dataPublicacao = parcela.dataPublicacaoDocumentoLegal;
            const dataMaximaInterrupcao = this.dataMaximaInterrupcao(parcela);
            const dataInterrupcaoForaPeriodo = !!dataInterrupcao && !Number.isNaN(dataInterrupcao.getTime()) && ((parcela.dataMinimaInterrupcao !== null && dataInterrupcao < parcela.dataMinimaInterrupcao) || (dataMaximaInterrupcao !== null && dataInterrupcao > dataMaximaInterrupcao));
            const justificativaInvalida = parcela.validacao.justificativaInterrupcaoInvalida || (justificativa.length > 0 && (justificativa.length < 10 || justificativa.length > 300));

            return !dataInterrupcao || Number.isNaN(dataInterrupcao.getTime()) || dataInterrupcaoForaPeriodo || !motivoLegal || !parcela.documentoLegal.trim() || !dataPublicacao || Number.isNaN(dataPublicacao.getTime()) || parcela.validacao.dataInterrupcaoInvalida || parcela.validacao.dataInterrupcaoForaPeriodoFerias || parcela.validacao.dataPublicacaoInvalida || justificativaInvalida || (motivoLegal === 'Necessidade do Serviço' && justificativa.length === 0);
        });
    }

    get extraordinario(): boolean {
        return this.dadosExercicio.extraordinario;
    }

    get permiteSomenteParcelasRaioX(): boolean {
        return this.exercicioSelecionado?.raioX === true;
    }

    get permiteLimiteParcelasPorDias(): boolean {
        const exercicio = this.exercicioSelecionado;
        return !!exercicio && (exercicio.siglaRegime === 'EST' || exercicio.siglaRegime === 'NES') && exercicio.codigoSituacaoFuncional === SITUACAO_FUNCIONAL_EXCEPCIONAL;
    }

    get limiteQuantidadeMaximaParcelas(): number {
        if (this.permiteSomenteParcelasRaioX) {
            return QUANTIDADE_PARCELAS_RAIO_X;
        }
        if (this.permiteLimiteParcelasPorDias) {
            return this.dadosExercicio.quantidadeDias ?? 1;
        }
        if (this.exercicioSelecionado?.siglaRegime === 'CLT') {
            return QUANTIDADE_MAXIMA_PARCELAS_CLT;
        }
        return QUANTIDADE_MAXIMA_PARCELAS_DEMAIS_REGIMES;
    }

    private atualizarOpcoesQuantidadeMaximaParcelas(): void {
        if (this.permiteSomenteParcelasRaioX) {
            this.opcoesQuantidadeMaximaParcelas = [QUANTIDADE_PARCELAS_RAIO_X];
            return;
        }

        this.opcoesQuantidadeMaximaParcelas = Array.from({ length: this.limiteQuantidadeMaximaParcelas }, (_, indice) => indice + 1);
    }

    get quantidadeMaximaParcelasSelecionada(): number {
        return this.dadosExercicio.quantidadeMaximaParcelas ?? this.limiteQuantidadeMaximaParcelas;
    }

    get quantidadeParcelasAtivas(): number {
        return this.parcelas.filter(parcela => !this.ehParcelaCancelada(parcela)).length;
    }

    private get quantidadeOrdensAtivas(): number {
        return new Set(this.parcelas.filter(parcela => !this.ehParcelaCancelada(parcela)).map(parcela => parcela.ordem)).size;
    }

    private get proximaOrdemParcela(): number {
        const maioresOrdens = this.parcelas.filter(parcela => !this.ehParcelaCancelada(parcela)).map(parcela => parcela.ordem);
        return Math.max(0, ...maioresOrdens) + 1;
    }

    get totalDiasParcelas(): number {
        return this.parcelas
            .filter(parcela => !this.ehParcelaCancelada(parcela))
            .reduce((total, parcela) => {
                const diasParcela = parcela.quantidadeDias ?? 0;
                const diasAbono = parcela.abonoPecuniario ? this.umTercoSaldoFerias : 0;
                return total + diasParcela + diasAbono;
            }, 0);
    }

    get umTercoSaldoFerias(): number {
        return Math.ceil((this.exercicioSelecionado?.saldoDias ?? 0) / 3);
    }

    get alocacaoDiasIncompleta(): boolean {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        return quantidadeDiasExercicio !== null && this.totalDiasParcelas !== quantidadeDiasExercicio;
    }

    get msgAlocacaoSaldoDiasIncompleta(): string {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        return formatarMensagem(Message.ERRO_SOMA_PARCELAS_DIFERENTE_DO_SALDO, {
            totalDias: this.totalDiasParcelas.toString(),
            saldoDias: (quantidadeDiasExercicio ?? 0).toString()
        });
    }

    get existemParcelasSobrepostas(): boolean {
        return this.parcelas.some((_, indice) => this.parcelaSobreposta(indice));
    }

    get existeParcelaForaDeOrdem(): boolean {
        return this.parcelas.some((_, indice) => this.parcelaForaDaOrdem(indice));
    }

    get quantidadeMaximaDiasParcela(): number {
        return this.dadosExercicio.quantidadeDias ?? QUANTIDADE_MAXIMA_DIAS_JUDICIAIS;
    }

    get dataInicioPeriodoProgramacaoMinima(): string {
        return this.paraDataIso(this.exercicioSelecionado?.dataInicioPeriodoProgramacaoDisponivel || this.exercicioSelecionado?.dataInicioPeriodoProgramacao);
    }

    get dataFimPeriodoProgramacaoMaxima(): string {
        const dataFimPeriodoProgramacao = this.extraordinario ? this.dadosExercicio.dataFimPeriodoProgramacao : this.exercicioSelecionado?.dataFimPeriodoProgramacaoDisponivel || this.exercicioSelecionado?.dataFimPeriodoProgramacao;

        return this.paraDataIso(dataFimPeriodoProgramacao);
    }

    get exibeAbonoPecuniario(): boolean {
        return this.exercicioSelecionado?.siglaRegime === 'CLT';
    }

    private get parcelasOriginais(): {
        parcela: ParcelaFerias;
        geracao: number;
        idParcelaPai: number | null;
    }[] {
        const parcelas: {
            parcela: ParcelaFerias;
            geracao: number;
            idParcelaPai: number | null;
        }[] = [];

        const achatarParcelas = (parcela: ParcelaFerias, geracao: number, idParcelaPai: number | null = null): void => {
            parcelas.push({
                parcela,
                geracao,
                idParcelaPai: parcela.idParcelaPai ?? idParcelaPai
            });

            if (parcela.parcelaFilha) {
                achatarParcelas(parcela.parcelaFilha, geracao + 1, parcela.id);
            }
        };

        [...(this.exercicioSelecionado?.parcelas ?? [])].sort((a, b) => a.ordem - b.ordem).forEach(parcela => achatarParcelas(parcela, 0));

        return parcelas;
    }

    campoExercicioInvalido(nome: CampoExercicio): boolean {
        return this.campoExercicioTemErro(nome) && (this.camposExercicioTocados.has(nome) || this.submetidoExercicio);
    }

    campoDeclaracaoNaoInformado(): boolean {
        return !this.dadosExercicio.declaracao;
    }

    campoExercicioObrigatorioNaoInformado(nome: CampoExercicio): boolean {
        if (!this.campoExercicioInvalido(nome)) {
            return false;
        }

        switch (nome) {
            case 'numeroProcesso':
                return this.dadosExercicio.numeroProcesso.trim().length === 0;
            case 'sentencaJudicial':
                return this.dadosExercicio.sentencaJudicial.trim().length === 0;
            case 'quantidadeDias':
                return this.dadosExercicio.quantidadeDias === null;
            case 'quantidadeMaximaParcelas':
                return this.dadosExercicio.quantidadeMaximaParcelas === null;
            case 'periodoAquisitivo':
                return !this.dadosExercicio.periodoAquisitivo;
            case 'extraordinario':
                return false;
            case 'dataFimPeriodoProgramacao':
                return this.extraordinario && this.dadosExercicio.dataFimPeriodoProgramacao.length === 0;
            case 'declaracaoAcaoJudicial':
                return !this.dadosExercicio.declaracaoAcaoJudicial;
            case 'declaracao':
                return !this.dadosExercicio.declaracao;
        }
    }

    private get existeCampoExercicioObrigatorioNaoInformado(): boolean {
        return this.campoExercicioObrigatorioNaoInformado('numeroProcesso') || this.campoExercicioObrigatorioNaoInformado('sentencaJudicial') || this.campoExercicioObrigatorioNaoInformado('quantidadeDias') || this.campoExercicioObrigatorioNaoInformado('quantidadeMaximaParcelas') || this.campoExercicioObrigatorioNaoInformado('periodoAquisitivo') || this.campoExercicioObrigatorioNaoInformado('dataFimPeriodoProgramacao') || this.campoExercicioObrigatorioNaoInformado('declaracaoAcaoJudicial');
    }

    campoParcelaInvalido(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela || (!parcela.tocado[nome] && !this.submetidoParcelas)) {
            return false;
        }

        return this.parcelaInvalida(indice, nome) || (nome === 'dataInicio' && (this.parcelaSobreposta(indice) || this.parcelaForaDaOrdem(indice)));
    }

    campoParcelaObrigatorioNaoInformado(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela || !this.campoParcelaInvalido(indice, nome)) {
            return false;
        }

        return nome === 'dataInicio' ? parcela.dataInicio.length === 0 : !parcela.quantidadeDiasDesabilitada && parcela.quantidadeDias === null;
    }

    private campoExercicioTemErro(nome: CampoExercicio): boolean {
        switch (nome) {
            case 'numeroProcesso': {
                const processo = this.dadosExercicio.numeroProcesso.trim();
                return processo.length === 0 || processo.length > 50 || REPETICAO_EXTENSA_PROCESSO.test(processo);
            }
            case 'sentencaJudicial': {
                const sentenca = this.dadosExercicio.sentencaJudicial.trim();
                return sentenca.length < 10 || sentenca.length > 300;
            }
            case 'quantidadeDias': {
                const quantidade = this.dadosExercicio.quantidadeDias;
                return quantidade === null || !Number.isInteger(quantidade) || quantidade < 1 || quantidade > QUANTIDADE_MAXIMA_DIAS_JUDICIAIS;
            }
            case 'quantidadeMaximaParcelas':
                return this.quantidadeMaximaParcelasInvalida;
            case 'periodoAquisitivo':
                return !this.ehPeriodoAquisitivo(this.dadosExercicio.periodoAquisitivo);
            case 'extraordinario':
                return false;
            case 'dataFimPeriodoProgramacao':
                return this.extraordinario && (!dataIsoValida(this.dadosExercicio.dataFimPeriodoProgramacao) || !this.validarDataFimPosteriorAoInicio());
            case 'declaracaoAcaoJudicial':
                return !this.dadosExercicio.declaracaoAcaoJudicial;
            case 'declaracao':
                return !this.dadosExercicio.declaracao;
        }
    }

    get exercicioInvalido(): boolean {
        return this.calculandoPeriodoProgramacao || this.erroCalculoPeriodoProgramacao || this.campoExercicioTemErro('numeroProcesso') || this.campoExercicioTemErro('sentencaJudicial') || this.campoExercicioTemErro('quantidadeDias') || this.campoExercicioTemErro('quantidadeMaximaParcelas') || this.campoExercicioTemErro('periodoAquisitivo') || this.campoExercicioTemErro('dataFimPeriodoProgramacao') || this.campoExercicioTemErro('declaracaoAcaoJudicial');
    }

    get quantidadeMaximaParcelasInvalida(): boolean {
        const valor = this.dadosExercicio.quantidadeMaximaParcelas;
        return valor === null || !Number.isInteger(valor) || (this.permiteSomenteParcelasRaioX ? valor !== QUANTIDADE_PARCELAS_RAIO_X : valor < 1 || valor > this.limiteQuantidadeMaximaParcelas);
    }

    get quantidadeMaximaParcelasIncompativel(): boolean {
        const valor = this.dadosExercicio.quantidadeMaximaParcelas;
        return valor !== null && valor < this.quantidadeOrdensAtivas;
    }

    get quantidadeMaximaParcelasRaioXInvalida(): boolean {
        const valor = this.dadosExercicio.quantidadeMaximaParcelas;
        return this.permiteSomenteParcelasRaioX && valor !== null && valor !== QUANTIDADE_PARCELAS_RAIO_X;
    }

    private validarDataFimPosteriorAoInicio(): boolean {
        const inicio = this.paraDataIso(this.exercicioSelecionado?.dataInicioPeriodoProgramacao);
        const fim = this.dadosExercicio.dataFimPeriodoProgramacao;
        return !!inicio && fim > inicio;
    }

    private campoParcelaTemErro(indice: number, nome: CampoParcela): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return false;
        }

        if (nome === 'dataInicio') {
            return !parcela.dataInicioDesabilitada && (!parcela.dataInicio || !dataIsoValida(parcela.dataInicio) || this.periodoProgramacaoIndisponivel(indice) || this.parcelaForaPeriodoProgramacao(indice));
        }

        return !parcela.quantidadeDiasDesabilitada && (parcela.quantidadeDias === null || !Number.isInteger(parcela.quantidadeDias) || parcela.quantidadeDias < 1 || parcela.quantidadeDias > this.quantidadeMaximaDiasParcela);
    }

    parcelaInvalida(indice: number, nome: CampoParcela): boolean {
        return this.campoParcelaTemErro(indice, nome);
    }

    periodoProgramacaoIndisponivel(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela?.dataInicio || !dataIsoValida(parcela.dataInicio)) {
            return false;
        }

        return !this.dataInicioPeriodoProgramacaoMinima || !this.dataFimPeriodoProgramacaoMaxima;
    }

    parcelaForaPeriodoProgramacao(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela?.dataInicio || !dataIsoValida(parcela.dataInicio)) {
            return false;
        }

        const inicioPeriodo = this.dataInicioPeriodoProgramacaoMinima;
        const fimPeriodo = this.dataFimPeriodoProgramacaoMaxima;
        return !!inicioPeriodo && !!fimPeriodo && (parcela.dataInicio < inicioPeriodo || parcela.dataInicio > fimPeriodo);
    }

    marcarCampoExercicioTocado(nome: CampoExercicio): void {
        this.camposExercicioTocados.add(nome);
    }

    marcarDeclaracaoTocada(): void {
        this.declaracaoTocada = true;
    }

    marcarCampoParcelaTocado(indice: number, nome: CampoParcela): void {
        const parcela = this.parcelas[indice];
        if (parcela) {
            parcela.tocado[nome] = true;
        }
    }

    registrarAlteracao(): void {
        this.houvePreenchimento = true;
    }

    alterarPeriodoAquisitivo(periodoAquisitivo: PeriodoAquisitivo | null): void {
        this.registrarAlteracao();
        this.calcularPeriodoProgramacao(periodoAquisitivo);
    }

    private calcularPeriodoProgramacao(periodoAquisitivo: PeriodoAquisitivo | null): void {
        const exercicio = this.exercicioSelecionado;
        if (!exercicio || (periodoAquisitivo !== 'ANC' && periodoAquisitivo !== 'PAQ')) {
            return;
        }

        this.periodoProgramacaoSubscription?.unsubscribe();
        const dataMinimaAnterior = this.dataInicioPeriodoProgramacaoMinimaCalendario;
        const idFerias = exercicio.idFerias;
        this.calculandoPeriodoProgramacao = true;
        this.erroCalculoPeriodoProgramacao = false;
        this.dataFimPeriodoProgramacaoCalculado = '';
        this.dadosExercicio.dataFimPeriodoProgramacao = '';
        this.dataFimPeriodoProgramacaoCalendario = null;
        this.exercicioSelecionado = {
            ...this.exercicioSelecionado,
            dataInicioPeriodoProgramacao: '',
            dataFimPeriodoProgramacao: ''
        };
        this.sincronizarPeriodoProgramacao();
        this.dataInicioPeriodoProgramacaoMinimaCalendario = this.paraDataCalendario(this.dataInicioPeriodoProgramacaoMinima);
        this.atualizarDataMinimaReprogramacaoParcelas(dataMinimaAnterior);

        this.periodoProgramacaoSubscription = this.exerciciosService.obterPeriodoProgramacaoFeriasEspeciais(idFerias, periodoAquisitivo, true).subscribe({
            next: periodo => {
                const exercicioAtual = this.exercicioSelecionado;
                if (!exercicioAtual || exercicioAtual.idFerias !== idFerias) {
                    return;
                }

                if (!dataIsoValida(periodo.dataInicioPeriodoProgramacao) || !dataIsoValida(periodo.dataFimPeriodoProgramacao) || periodo.dataFimPeriodoProgramacao <= periodo.dataInicioPeriodoProgramacao) {
                    this.falharCalculoPeriodoProgramacao();
                    return;
                }

                const novaDataInicio = this.paraDataCalendario(periodo.dataInicioPeriodoProgramacao);
                const novaDataFim = this.paraDataCalendario(periodo.dataFimPeriodoProgramacao);
                if (!novaDataInicio || !novaDataFim) {
                    this.falharCalculoPeriodoProgramacao();
                    return;
                }

                this.exercicioSelecionado = {
                    ...exercicioAtual,
                    dataInicioPeriodoProgramacao: periodo.dataInicioPeriodoProgramacao,
                    dataFimPeriodoProgramacao: periodo.dataFimPeriodoProgramacao
                };
                this.dadosExercicio.dataFimPeriodoProgramacao = periodo.dataFimPeriodoProgramacao;
                this.dataFimPeriodoProgramacaoCalculado = periodo.dataFimPeriodoProgramacao;
                this.dataFimPeriodoProgramacaoCalendario = novaDataFim;
                this.dataInicioPeriodoProgramacaoMinimaCalendario = this.paraDataCalendario(this.dataInicioPeriodoProgramacaoMinima);
                this.calculandoPeriodoProgramacao = false;
                this.sincronizarPeriodoProgramacao();
                this.atualizarDataMinimaReprogramacaoParcelas(dataMinimaAnterior);
            },
            error: () => this.falharCalculoPeriodoProgramacao()
        });
    }

    private falharCalculoPeriodoProgramacao(): void {
        this.calculandoPeriodoProgramacao = false;
        this.erroCalculoPeriodoProgramacao = true;
        this.dataFimPeriodoProgramacaoCalculado = '';
        this.dadosExercicio.dataFimPeriodoProgramacao = '';
        this.dataFimPeriodoProgramacaoCalendario = null;
        if (this.exercicioSelecionado) {
            this.exercicioSelecionado = {
                ...this.exercicioSelecionado,
                dataInicioPeriodoProgramacao: '',
                dataFimPeriodoProgramacao: ''
            };
        }
        this.dataInicioPeriodoProgramacaoMinimaCalendario = this.paraDataCalendario(this.dataInicioPeriodoProgramacaoMinima);
        this.sincronizarPeriodoProgramacao();
    }

    private atualizarDataMinimaReprogramacaoParcelas(dataMinimaAnterior: Date | null): void {
        const novaDataMinima = this.dataInicioPeriodoProgramacaoMinimaCalendario;
        this.parcelas.forEach(parcela => {
            if (parcela.dataMinimaReprogramacao === null || (dataMinimaAnterior && parcela.dataMinimaReprogramacao.getTime() === dataMinimaAnterior.getTime())) {
                parcela.dataMinimaReprogramacao = novaDataMinima;
            }
        });
    }

    validarInputQuantidadeDias(event: Event): void {
        const input = event.target as HTMLInputElement;
        const valor = input.value.replace(/\D/g, '').slice(0, 2);
        input.value = valor;

        this.dadosExercicio.quantidadeDias = valor === '' ? null : Number(valor);
        this.atualizarOpcoesQuantidadeMaximaParcelas();
        this.registrarAlteracao();
    }

    alterarExtraordinario(): void {
        this.registrarAlteracao();

        //this.dadosExercicio.extraordinario = !this.dadosExercicio.extraordinario;

        if (!this.extraordinario) {
            this.dadosExercicio.dataFimPeriodoProgramacao = this.dataFimPeriodoProgramacaoCalculado;
            this.dataFimPeriodoProgramacaoCalendario = this.paraDataCalendario(this.dataFimPeriodoProgramacaoCalculado);
        }

        this.sincronizarPeriodoProgramacao();
    }

    atualizarDataFimPeriodoProgramacao(dataFim: Date | null): void {
        const dataFimIso = dataFim ? this.paraDataIso(dataFim) : '';
        if (dataFimIso && !dataIsoValida(dataFimIso)) {
            return;
        }

        this.dataFimPeriodoProgramacaoCalendario = dataFim;
        this.dadosExercicio.dataFimPeriodoProgramacao = dataFimIso;
        this.sincronizarPeriodoProgramacao();
        this.registrarAlteracao();
    }

    private sincronizarPeriodoProgramacao(): void {
        if (this.exercicioSelecionado) {
            this.exercicioSelecionado = {
                ...this.exercicioSelecionado,
                dataFimPeriodoProgramacao: this.dadosExercicio.dataFimPeriodoProgramacao
            };
        }

        this.dataFimPeriodoProgramacaoMaximaCalendario = this.paraDataCalendario(this.dataFimPeriodoProgramacaoMaxima);
    }

    finalizarDataFimPeriodoProgramacao(event: Event): void {
        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataFimDigitada = getValidDateDDMMYYYY(valorDigitado);
        const dataFimIso = dataFimDigitada ? this.paraDataIso(dataFimDigitada) : '';
        const dataFimCalendarioIso = this.dataFimPeriodoProgramacaoCalendario ? this.paraDataIso(this.dataFimPeriodoProgramacaoCalendario) : '';

        if (this.dadosExercicio.dataFimPeriodoProgramacao !== dataFimIso || dataFimCalendarioIso !== dataFimIso) {
            this.atualizarDataFimPeriodoProgramacao(dataFimDigitada);
        }
        this.marcarCampoExercicioTocado('dataFimPeriodoProgramacao');
    }

    alterarQuantidadeMaximaParcelas(): void {
        this.registrarAlteracao();
    }

    dataFimParcela(indice: number): string {
        const parcela = this.parcelas[indice];
        const dataInicio = parcela?.dataInicio;
        const quantidadeDias = parcela?.quantidadeDias;

        if (!dataInicio || !quantidadeDias || quantidadeDias < 1) {
            return '';
        }

        const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataInicio);
        if (!partes) {
            return '';
        }

        const data = new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]) + quantidadeDias - 1);

        return this.paraDataIso(data);
    }

    alterarDataInicioParcela(indice: number): void {
        if (this.parcelas[indice]?.dataInicioDesabilitada) {
            return;
        }

        this.registrarAlteracao();
        this.criarParcelaParaDiasRestantes();
    }

    atualizarDataInicioParcela(indice: number, dataInicio: Date | null): void {
        const parcela = this.parcelas[indice];
        if (!parcela || parcela.dataInicioDesabilitada) {
            return;
        }

        const dataInicioIso = dataInicio ? this.paraDataIso(dataInicio) : '';
        if (dataInicioIso && !dataIsoValida(dataInicioIso)) {
            return;
        }

        const dataAlterada = parcela.dataInicio !== dataInicioIso;
        parcela.dataInicioCalendarioControl.setValue(dataInicio, {
            emitEvent: false
        });
        parcela.dataInicio = dataInicioIso;
        parcela.dataMinimaInterrupcao = this.calcularDataMinimaInterrupcao(dataInicioIso);
        if (dataAlterada) {
            this.alterarDataInicioParcela(indice);
        } else {
            this.registrarAlteracao();
        }
    }

    finalizarDataInicioParcela(indice: number, event: Event): void {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return;
        }

        const valorDigitado = (event.target as HTMLInputElement).value;
        const dataInicioDigitada = getValidDateDDMMYYYY(valorDigitado);
        const dataInicioIso = dataInicioDigitada ? this.paraDataIso(dataInicioDigitada) : '';
        const dataInicioCalendarioIso = parcela.dataInicioCalendarioControl.value ? this.paraDataIso(parcela.dataInicioCalendarioControl.value) : '';

        if (parcela.dataInicio !== dataInicioIso || dataInicioCalendarioIso !== dataInicioIso) {
            this.atualizarDataInicioParcela(indice, dataInicioDigitada);
        }
        this.marcarCampoParcelaTocado(indice, 'dataInicio');
    }

    atualizarQuantidadeDiasParcela(indice: number, event: Event): void {
        const parcela = this.parcelas[indice];
        if (!parcela || parcela.quantidadeDiasDesabilitada) {
            return;
        }

        const valor = (event.target as HTMLInputElement).value;
        parcela.quantidadeDias = valor === '' ? null : Number(valor);
        this.registrarAlteracao();
    }

    alterarQuantidadeDiasParcela(indice: number): void {
        if (this.parcelas[indice]?.quantidadeDiasDesabilitada) {
            return;
        }

        this.registrarAlteracao();
        this.criarParcelaParaDiasRestantes();
    }

    permiteExcluirParcela(indice: number): boolean {
        const parcela = this.parcelas[indice];
        if (!parcela) {
            return false;
        }

        return this.permiteExcluirParcelaFilhaInterrompida(parcela) || (!this.possuiParcelaFilha(parcela) && (parcela.idParcelaPai === null || this.ehParcelaCancelada(parcela)) && this.parcelas.length > 1 && this.quantidadeParcelasAtivas > 0 && (this.ehParcelaCancelada(parcela) || this.indiceUltimaParcelaRedistribuivel(indice) !== -1));
    }

    private get permiteIncluirParcelaAutomaticamente(): boolean {
        return this.quantidadeOrdensAtivas < this.quantidadeMaximaParcelasSelecionada;
    }

    private criarParcelaParaDiasRestantes(): void {
        const quantidadeDiasExercicio = this.dadosExercicio.quantidadeDias;
        if (quantidadeDiasExercicio === null || this.totalDiasParcelas <= 0 || this.totalDiasParcelas >= quantidadeDiasExercicio || !this.permiteIncluirParcelaAutomaticamente) {
            return;
        }

        this.parcelas.push(
            this.criarParcela({
                ordem: this.proximaOrdemParcela,
                dataInicio: '',
                quantidadeDias: quantidadeDiasExercicio - this.totalDiasParcelas,
                situacao: SituacaoParcelaFeriasEnum.NAO_SOLICITADA.descricao
            })
        );
        this.registrarAlteracao();
    }

    excluirParcela(indice: number): void {
        if (!this.permiteExcluirParcela(indice)) {
            return;
        }

        const parcelaExcluida = this.parcelas[indice];
        if (parcelaExcluida.geracao > 0 && !this.ehParcelaCancelada(parcelaExcluida)) {
            this.excluirParcelaFilhaInterrompida(indice);
            return;
        }

        const diasExcluidos = this.ehParcelaCancelada(parcelaExcluida) ? 0 : (parcelaExcluida.quantidadeDias ?? 0) + (parcelaExcluida.abonoPecuniario ? this.umTercoSaldoFerias : 0);
        const indiceRedistribuicao = this.indiceUltimaParcelaRedistribuivel(indice);
        const indiceRedistribuicaoFinal = indiceRedistribuicao > indice ? indiceRedistribuicao - 1 : indiceRedistribuicao;

        this.parcelas.splice(indice, 1);
        this.reordenarOrdensParcelas();

        if (diasExcluidos > 0 && indiceRedistribuicaoFinal !== -1) {
            const parcelaRedistribuicao = this.parcelas[indiceRedistribuicaoFinal];
            parcelaRedistribuicao.quantidadeDias = (parcelaRedistribuicao.quantidadeDias ?? 0) + diasExcluidos;
        }

        this.registrarAlteracao();
    }

    private excluirParcelaFilhaInterrompida(indice: number): void {
        const parcelaFilha = this.parcelas[indice];
        const parcelaPai = parcelaFilha && this.parcelaPaiInterrompida(parcelaFilha);
        const indiceParcelaPai = parcelaPai ? this.parcelas.indexOf(parcelaPai) : -1;
        if (!parcelaFilha || !parcelaPai || !this.permiteExcluirParcelaFilhaInterrompida(parcelaFilha) || indiceParcelaPai === -1) {
            return;
        }

        parcelaPai.quantidadeDias = (parcelaPai.quantidadeDias ?? 0) + (parcelaFilha.quantidadeDias ?? 0) + (parcelaFilha.abonoPecuniario ? this.umTercoSaldoFerias : 0);
        const dataFimRestaurada = this.paraDataCalendario(this.dataFimParcela(indiceParcelaPai));
        parcelaPai.situacao = this.situacaoAposRecomporParcela(dataFimRestaurada);
        this.limparDadosInterrupcao(parcelaPai);
        this.dadosOriginaisInterrupcao.delete(parcelaPai);

        const permiteEditar = this.situacaoPermiteEdicao(parcelaPai.situacao);
        parcelaPai.dataInicioDesabilitada = !permiteEditar;
        if (permiteEditar) {
            parcelaPai.dataInicioCalendarioControl.enable({ emitEvent: false });
        } else {
            parcelaPai.dataInicioCalendarioControl.disable({ emitEvent: false });
        }
        parcelaPai.quantidadeDiasDesabilitada = !permiteEditar || parcelaPai.quantidadeDiasAutomatica;

        this.parcelas.splice(indice, 1);
        this.registrarAlteracao();
    }

    private situacaoAposRecomporParcela(dataFim: Date | null): string {
        if (!dataFim) {
            return SituacaoParcelaFeriasEnum.USUFRUIDA.descricao;
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const dataFimSemHora = new Date(dataFim);
        dataFimSemHora.setHours(0, 0, 0, 0);

        return hoje > dataFimSemHora ? SituacaoParcelaFeriasEnum.USUFRUIDA.descricao : SituacaoParcelaFeriasEnum.EM_GOZO.descricao;
    }

    private reordenarOrdensParcelas(): void {
        let proximaOrdem = 1;
        const ordensPorId = new Map<number, number>();

        this.parcelas.forEach(parcela => {
            if (parcela.geracao > 0 && parcela.idParcelaPai !== null) {
                const ordemPai = ordensPorId.get(parcela.idParcelaPai);
                if (ordemPai !== undefined) {
                    parcela.ordem = ordemPai;
                }
            } else if (!this.ehParcelaCancelada(parcela)) {
                parcela.ordem = proximaOrdem;
                proximaOrdem += 1;
            }

            if (parcela.id !== null) {
                ordensPorId.set(parcela.id, parcela.ordem);
            }
        });
    }

    confirmarExcluirParcela(indice: number): void {
        if (!this.permiteExcluirParcela(indice)) {
            return;
        }

        this.confirmationService.confirm({
            key: 'confirmPadrao',
            header: 'Confirmação de exclusão',
            message: 'Deseja realmente excluir a parcela selecionada?',
            accept: () => this.excluirParcela(indice)
        });
    }

    private indiceUltimaParcelaRedistribuivel(indiceExcluido: number): number {
        for (let indice = this.parcelas.length - 1; indice >= 0; indice--) {
            if (indice !== indiceExcluido && !this.ehParcelaCancelada(this.parcelas[indice]) && !this.parcelas[indice].dataInicioDesabilitada) {
                return indice;
            }
        }

        return -1;
    }

    avancarWizard(): void {
        this.submetidoExercicio = true;
        this.clearMessages();

        if (this.exercicioInvalido || !this.exercicioSelecionado) {
            if (this.existeCampoExercicioObrigatorioNaoInformado) {
                this.getAlertaMsgService().error(Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS);
            }
            return;
        }

        this.wizardActiveIndex = 1;
        this.rolarParaTopo();
    }

    voltarWizard(): void {
        this.wizardActiveIndex = 0;
        this.rolarParaTopo();
    }

    concluirWizard(): void {
        this.submetidoExercicio = true;
        this.submetidoParcelas = true;

        if (this.exercicioInvalido || !this.exercicioSelecionado) {
            this.wizardActiveIndex = 0;
            return;
        }

        let podeConcluir = true;

        if (this.campoDeclaracaoNaoInformado()) {
            this.getAlertaMsgService().error(Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS);
            podeConcluir = false;
        }

        if (this.quantidadeMaximaParcelasIncompativel) {
            this.getAlertaMsgService().error('O Máximo de Parcelas de Férias deve ser coerente com a quantidade de parcelas do exercício.');
            podeConcluir = false;
        }

        if (this.quantidadeParcelasAtivas === 0) {
            this.getAlertaMsgService().error('Informe ao menos uma parcela para concluir a operação.');
            podeConcluir = false;
        }

        if (this.alocacaoDiasIncompleta) {
            this.getAlertaMsgService().error(this.msgAlocacaoSaldoDiasIncompleta);
            podeConcluir = false;
        }

        if (this.existeInterrupcaoInvalida) {
            this.getAlertaMsgService().error(Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS);
            podeConcluir = false;
        }

        if (this.existemParcelasSobrepostas) {
            this.getAlertaMsgService().error('Não é permitida sobreposição entre parcelas do mesmo exercício ou com parcelas de exercícios subsequentes.');
            podeConcluir = false;
        }

        if (this.existeParcelaForaDeOrdem) {
            this.getAlertaMsgService().error('As parcelas devem respeitar a ordem cronológica crescente.');
            podeConcluir = false;
        }

        const existeParcelaInvalida = this.parcelas.some((_, indice) => this.parcelaInvalida(indice, 'dataInicio') || this.parcelaInvalida(indice, 'quantidadeDias'));
        if (existeParcelaInvalida) {
            podeConcluir = false;
        }

        if (!podeConcluir) {
            return;
        }

        const dados = this.montarPayload();
        if (!dados) {
            return;
        }

        this.exerciciosService.feriasEspeciaisJudiciaisComParcelas(this.exercicioSelecionado.idFerias, dados).subscribe({
            next: () => {
                this.getAlertaMsgService().setAwatingSuccessMessage('Férias Especiais Judiciais com Parcelas registradas com sucesso.');
                this.router.navigate([ROTA_CONSULTAR_EXERCICIOS]);
            },
            error: error => this.errorHandler.handleError(error)
        });
    }

    confirmarCancelarWizard() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar(): void {
        this.router.navigate([ROTA_CONSULTAR_EXERCICIOS]);
    }

    private montarPayload(): FeriasEspeciaisJudiciaisComParcelas | null {
        const valores = this.dadosExercicio;

        if (valores.quantidadeDias === null || valores.quantidadeMaximaParcelas === null || !this.ehPeriodoAquisitivo(valores.periodoAquisitivo) || (valores.extraordinario && !valores.dataFimPeriodoProgramacao)) {
            this.submetidoExercicio = true;
            return null;
        }

        const dados: FeriasEspeciaisJudiciaisComParcelas = {
            numeroProcesso: valores.numeroProcesso.trim(),
            sentencaJudicial: valores.sentencaJudicial.trim(),
            quantidadeDias: valores.quantidadeDias,
            quantidadeMaximaParcelas: valores.quantidadeMaximaParcelas,
            periodoAquisitivo: valores.periodoAquisitivo,
            extraordinario: valores.extraordinario,
            declaracaoAcaoJudicial: valores.declaracaoAcaoJudicial,
            parcelas: this.parcelas.filter(parcela => !this.ehParcelaCancelada(parcela)).map(parcela => this.montarParcela(parcela))
        };

        if (valores.extraordinario) {
            dados.dataFimPeriodoProgramacao = valores.dataFimPeriodoProgramacao;
        }

        return dados;
    }

    private montarParcela(parcela: ParcelaJudicialComParcelas): ParcelaFeriasEspecialJudicialComParcelas {
        const dados: ParcelaFeriasEspecialJudicialComParcelas = {
            geracao: parcela.geracao,
            ordem: parcela.ordem,
            dataInicio: parcela.dataInicio,
            quantidadeDias: parcela.quantidadeDias ?? 0,
            adiantamentoSalarial: parcela.adiantamentoSalarial,
            abonoPecuniario: parcela.abonoPecuniario,
            adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro
        };

        if (parcela.id !== null) {
            dados.id = parcela.id;
        }

        if (parcela.idParcelaPai !== null) {
            dados.idParcelaPai = parcela.idParcelaPai;
        }

        if (parcela.idTemporario !== null) {
            dados.idTemporario = parcela.idTemporario;
        }

        if (parcela.idTemporarioParcelaPai !== null) {
            dados.idTemporarioParcelaPai = parcela.idTemporarioParcelaPai;
        }

        if (parcela.parcelasDesconto !== null) {
            dados.parcelasDesconto = parcela.parcelasDesconto;
        }

        const situacao = SituacaoParcelaFeriasEnum.fromDesc(parcela.situacao) ?? SituacaoParcelaFeriasEnum.fromId(parcela.situacao);
        if (parcela.selecionadaParaInterrupcao || situacao === SituacaoParcelaFeriasEnum.INTERROMPIDA) {
            dados.dataInterrupcao = parcela.dataInterrupcao;
            dados.motivoLegal = parcela.motivoLegal;
            dados.justificativaInterrupcao = parcela.justificativaInterrupcao;
            dados.documentoLegal = parcela.documentoLegal;
            dados.dataPublicacaoDocumentoLegal = parcela.dataPublicacaoDocumentoLegal;
        }

        return dados;
    }

    private carregarParcelasDoExercicio(): void {
        this.parcelas.length = 0;
        this.parcelas.push(
            ...this.parcelasOriginais.map(({ parcela, geracao, idParcelaPai }, indice) =>
                this.criarParcela({
                    id: parcela.id ?? null,
                    idParcelaPai,
                    ordem: parcela.ordem ?? indice + 1,
                    geracao,
                    dataInicio: this.paraDataIso(parcela.dataInicio),
                    quantidadeDias: parcela.quantidadeDias ?? null,
                    adiantamentoSalarial: parcela.adiantamentoSalarial === true,
                    abonoPecuniario: parcela.abonoPecuniario === true,
                    adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro === true,
                    parcelasDesconto: parcela.parcelasDesconto ?? null,
                    situacao: parcela.situacao ?? '',
                    dataInterrupcao: parcela.dataInterrupcao,
                    motivoLegal: parcela.motivoLegal ?? '',
                    justificativaInterrupcao: parcela.justificativaInterrupcao ?? '',
                    documentoLegal: parcela.documentoLegal?.toString() ?? '',
                    dataPublicacaoDocumentoLegal: parcela.dataPublicacaoDocumentoLegal
                })
            )
        );
        this.parcelas.sort((a, b) => a.ordem - b.ordem || a.geracao - b.geracao);
    }

    private criarParcela(valores: {
        id?: number | null;
        idParcelaPai?: number | null;
        idTemporario?: string | null;
        idTemporarioParcelaPai?: string | null;
        ordem: number;
        geracao?: number;
        dataInicio?: string;
        quantidadeDias?: number | null;
        adiantamentoSalarial?: boolean;
        abonoPecuniario?: boolean;
        adiantamentoDecimoTerceiro?: boolean;
        parcelasDesconto?: number | null;
        quantidadeDiasAutomatica?: boolean;
        situacao?: string;
        dataInterrupcao?: Date;
        motivoLegal?: string;
        justificativaInterrupcao?: string;
        documentoLegal?: string;
        dataPublicacaoDocumentoLegal?: Date;
        dataMinimaReprogramacao?: Date | null;
    }): ParcelaJudicialComParcelas {
        const parcelaExistente = valores.id !== undefined && valores.id !== null;
        const quantidadeDiasAutomatica = valores.quantidadeDiasAutomatica === true;
        const situacaoPermiteEdicao = this.situacaoPermiteEdicao(valores.situacao ?? '');
        const podeEditar = !parcelaExistente || situacaoPermiteEdicao;
        const dataInicio = valores.dataInicio ?? '';
        const dataInicioDesabilitada = !podeEditar;

        return {
            id: valores.id ?? null,
            idParcelaPai: valores.idParcelaPai ?? null,
            idTemporario: parcelaExistente ? null : (valores.idTemporario ?? generateUID()),
            idTemporarioParcelaPai: valores.idTemporarioParcelaPai ?? null,
            ordem: valores.ordem,
            geracao: valores.geracao ?? 0,
            dataInicio,
            dataInicioCalendarioControl: new FormControl<Date | null>({
                value: this.paraDataCalendario(dataInicio),
                disabled: dataInicioDesabilitada
            }),
            quantidadeDias: valores.quantidadeDias ?? null,
            adiantamentoSalarial: valores.adiantamentoSalarial === true,
            abonoPecuniario: valores.abonoPecuniario === true,
            adiantamentoDecimoTerceiro: valores.adiantamentoDecimoTerceiro === true,
            parcelasDesconto: valores.parcelasDesconto ?? null,
            situacao: valores.situacao ?? '',
            dataInicioDesabilitada,
            quantidadeDiasDesabilitada: !podeEditar || quantidadeDiasAutomatica,
            quantidadeDiasAutomatica,
            selecionadaParaInterrupcao: false,
            dataInterrupcao: valores.dataInterrupcao,
            motivoLegal: valores.motivoLegal ?? '',
            justificativaInterrupcao: valores.justificativaInterrupcao ?? '',
            documentoLegal: valores.documentoLegal ?? '',
            dataPublicacaoDocumentoLegal: valores.dataPublicacaoDocumentoLegal,
            dataMinimaInterrupcao: this.calcularDataMinimaInterrupcao(dataInicio),
            dataMinimaReprogramacao: valores.dataMinimaReprogramacao ?? this.dataInicioPeriodoProgramacaoMinimaCalendario,
            validacao: {
                dataInterrupcaoInvalida: false,
                dataInterrupcaoForaPeriodoFerias: false,
                dataPublicacaoInvalida: false,
                justificativaInterrupcaoInvalida: false
            },
            tocado: {
                dataInicio: false,
                quantidadeDias: false
            }
        };
    }

    private ehParcelaCancelada(parcela: ParcelaJudicialComParcelas): boolean {
        return parcela.situacao === SituacaoParcelaFeriasEnum.CANCELADA.id || parcela.situacao === SituacaoParcelaFeriasEnum.CANCELADA.descricao;
    }

    private situacaoPermiteEdicao(situacao: string): boolean {
        const valor = SituacaoParcelaFeriasEnum.fromDesc(situacao) ?? SituacaoParcelaFeriasEnum.fromId(situacao);

        return valor === SituacaoParcelaFeriasEnum.HOMOLOGADA || valor === SituacaoParcelaFeriasEnum.EM_GOZO || valor === SituacaoParcelaFeriasEnum.USUFRUIDA;
    }

    situacaoParcela(indice: number): { descricao: string; codigoCor: string } {
        const situacao = this.parcelas[indice].situacao;
        const valor = SituacaoParcelaFeriasEnum.fromDesc(situacao) ?? SituacaoParcelaFeriasEnum.fromId(situacao);

        return {
            descricao: valor?.descricao || situacao || 'Nova parcela',
            codigoCor: valor?.extras?.codigoCor ?? SituacaoParcelaFeriasEnum.CANCELADA.extras.codigoCor
        };
    }

    parcelaSobreposta(indice: number): boolean {
        const intervalo = this.intervaloParcela(indice);
        if (!intervalo) {
            return false;
        }

        return this.parcelas.some((_, outroIndice) => {
            if (outroIndice === indice) {
                return false;
            }

            const outroIntervalo = this.intervaloParcela(outroIndice);
            return outroIntervalo !== null && intervalo.inicio <= outroIntervalo.fim && outroIntervalo.inicio <= intervalo.fim;
        });
    }

    parcelaForaDaOrdem(indice: number): boolean {
        const intervalo = this.intervaloParcela(indice);
        if (!intervalo) {
            return false;
        }

        for (let indiceAnterior = indice - 1; indiceAnterior >= 0; indiceAnterior--) {
            const intervaloAnterior = this.intervaloParcela(indiceAnterior);
            if (intervaloAnterior) {
                return intervalo.inicio <= intervaloAnterior.inicio;
            }
        }

        return false;
    }

    private intervaloParcela(indice: number): { inicio: string; fim: string } | null {
        const parcela = this.parcelas[indice];
        const dataInicio = parcela?.dataInicio;
        const quantidadeDias = parcela?.quantidadeDias;

        if (!parcela || this.ehParcelaCancelada(parcela) || !dataInicio || !dataIsoValida(dataInicio) || quantidadeDias === null || quantidadeDias < 1) {
            return null;
        }

        const dataFim = this.dataFimParcela(indice);
        return dataFim ? { inicio: dataInicio, fim: dataFim } : null;
    }

    private paraDataIso(valor: Date | string | undefined): string {
        if (!valor) {
            return '';
        }

        if (valor instanceof Date) {
            const ano = valor.getFullYear();
            const mes = `${valor.getMonth() + 1}`.padStart(2, '0');
            const dia = `${valor.getDate()}`.padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        }

        return String(valor).slice(0, 10);
    }

    private paraDataCalendario(valor: string): Date | null {
        return dataIsoValida(valor) ? getDateFromYYYYMMDD(valor) : null;
    }

    private ehPeriodoAquisitivo(valor: string | null): valor is PeriodoAquisitivo {
        return valor === 'ANC' || valor === 'PAQ';
    }
}
